/*!
 * Xtenia Score - Result Extras
 * Copyright (c) 2026 Xtenia. All rights reserved.
 * https://www.xtenia.com
 *
 * Adds the comparison code block, compare-with-friend widget,
 * PDF download, and friend-challenge share button to the result screen.
 */

(function() {
  'use strict';

  window.WD = window.WD || {};
  window.WD.score = window.WD.score || {};
  var WS = window.WD.score;

  function el(tag, className, attrs) {
    var n = document.createElement(tag);
    if (className) n.className = className;
    if (attrs) for (var k in attrs) {
      if (k === 'text') n.textContent = attrs[k];
      else if (k === 'html') n.innerHTML = attrs[k];
      else n.setAttribute(k, attrs[k]);
    }
    return n;
  }

  function t(key, vars) {
    var raw = (typeof WS.t === 'function') ? WS.t(key) : key;
    if (vars) {
      for (var k in vars) {
        raw = raw.split('{' + k + '}').join(vars[k]);
      }
    }
    return raw;
  }

  /**
   * Render the score code block: shows the user's unique code with
   * Copy and Challenge-a-Friend actions.
   */
  WS.renderCodeBlock = function(result, answers) {
    var code = (WS.state && WS.state.scoreCode)
            || (WS.generateScoreCode ? WS.generateScoreCode(result) : null);
    if (!code) return el('div'); // graceful no-op

    var wrap = el('div', 'ws-result-code');

    var label = el('div', 'ws-result-code-label', { text: t('code_label') });
    wrap.appendChild(label);

    var value = el('div', 'ws-result-code-value', { text: code });
    wrap.appendChild(value);

    var hint = el('div', 'ws-result-code-hint', { text: t('code_hint') });
    wrap.appendChild(hint);

    var actions = el('div', 'ws-result-code-actions');

    // Copy button
    var copyBtn = el('button', 'ws-result-code-btn', {
      type: 'button',
      text: t('code_copy')
    });
    copyBtn.addEventListener('click', function() {
      copyToClipboard(code, function(ok) {
        if (ok) {
          copyBtn.textContent = t('code_copied');
          copyBtn.classList.add('ws-result-code-copied');
          setTimeout(function() {
            copyBtn.textContent = t('code_copy');
            copyBtn.classList.remove('ws-result-code-copied');
          }, 2000);
        }
      });
    });
    actions.appendChild(copyBtn);

    // Challenge-a-friend button (uses Web Share API or falls back to copy)
    var challengeBtn = el('button', 'ws-result-code-btn', {
      type: 'button',
      text: t('code_share')
    });
    challengeBtn.addEventListener('click', function() {
      shareWithFriend(result, code);
    });
    actions.appendChild(challengeBtn);

    // PDF download button
    var pdfBtn = el('button', 'ws-result-code-btn', {
      type: 'button',
      text: t('pdf_btn')
    });
    pdfBtn.addEventListener('click', function() {
      pdfBtn.textContent = t('pdf_generating');
      pdfBtn.disabled = true;
      generateAndDownloadPDF(result, answers, code, function() {
        pdfBtn.textContent = t('pdf_btn');
        pdfBtn.disabled = false;
      });
    });
    actions.appendChild(pdfBtn);

    wrap.appendChild(actions);
    return wrap;
  };

  /**
   * Render the "Compare with a friend" widget.
   */
  WS.renderCompareSection = function(myResult) {
    var section = el('div', 'ws-compare-section');

    var title = el('h3', 'ws-compare-title', { text: t('compare_title') });
    section.appendChild(title);

    var sub = el('p', 'ws-compare-sub', { text: t('compare_sub') });
    section.appendChild(sub);

    var inputRow = el('div', 'ws-compare-input-row');
    var input = el('input', 'ws-compare-input', {
      type: 'text',
      placeholder: t('compare_input_placeholder'),
      maxlength: 20,
      autocomplete: 'off',
      spellcheck: 'false'
    });
    var btn = el('button', 'ws-compare-btn', {
      type: 'button',
      text: t('compare_btn')
    });
    inputRow.appendChild(input);
    inputRow.appendChild(btn);
    section.appendChild(inputRow);

    var errorEl = el('div', 'ws-compare-error');
    errorEl.style.display = 'none';
    section.appendChild(errorEl);

    var panelContainer = el('div', 'ws-compare-panel-container');
    section.appendChild(panelContainer);

    function doCompare() {
      var raw = input.value.trim();
      if (!raw) return;

      var parsed = WS.parseScoreCode ? WS.parseScoreCode(raw) : null;
      if (!parsed) {
        errorEl.textContent = t('compare_invalid');
        errorEl.style.display = '';
        panelContainer.innerHTML = '';
        return;
      }

      errorEl.style.display = 'none';
      var cmp = WS.compareResults(myResult, parsed);
      panelContainer.innerHTML = '';
      panelContainer.appendChild(renderComparePanel(cmp));
    }

    btn.addEventListener('click', doCompare);
    input.addEventListener('keydown', function(e) {
      if (e.key === 'Enter') doCompare();
    });

    return section;
  };

  function renderComparePanel(cmp) {
    var panel = el('div', 'ws-compare-panel');

    var grid = el('div', 'ws-compare-grid');

    // Left col - You
    var youCol = el('div', 'ws-compare-col');
    youCol.appendChild(el('div', 'ws-compare-col-label', { text: t('compare_you') }));
    youCol.appendChild(el('div', 'ws-compare-col-score', { text: String(cmp.mine.total) }));
    youCol.appendChild(el('div', 'ws-compare-col-tier', { text: t('tier_' + cmp.mine.tier) }));
    youCol.appendChild(el('div', 'ws-compare-col-archetype', { text: t('archetype_' + cmp.mine.archetype) }));
    grid.appendChild(youCol);

    // Middle - vs
    var vs = el('div', 'ws-compare-vs', { text: t('compare_vs') });
    grid.appendChild(vs);

    // Right col - Them
    var themCol = el('div', 'ws-compare-col');
    themCol.appendChild(el('div', 'ws-compare-col-label', { text: t('compare_them') }));
    themCol.appendChild(el('div', 'ws-compare-col-score', { text: String(cmp.theirs.total) }));
    themCol.appendChild(el('div', 'ws-compare-col-tier', { text: t('tier_' + cmp.theirs.tier) }));
    themCol.appendChild(el('div', 'ws-compare-col-archetype', { text: t('archetype_' + cmp.theirs.archetype) }));
    grid.appendChild(themCol);

    panel.appendChild(grid);

    // Pillar breakdown
    var pillars = el('div', 'ws-compare-pillars');
    var pNames = ['strength', 'skill', 'mobility', 'endurance', 'movement'];
    for (var i = 0; i < pNames.length; i++) {
      var p = pNames[i];
      var data = cmp.pillars[p];
      if (!data) continue;
      var pCard = el('div', 'ws-compare-pillar');
      pCard.appendChild(el('div', 'ws-compare-pillar-name', { text: t('pillar_' + p) }));
      var valsDiv = el('div', 'ws-compare-pillar-vals');
      var youSpan = el('span', data.winner === 'mine' ? 'ws-compare-pillar-winner' : '', { text: String(data.mine) });
      var sep = el('span', '', { text: ' / ' });
      var themSpan = el('span', data.winner === 'theirs' ? 'ws-compare-pillar-winner' : '', { text: String(data.theirs) });
      valsDiv.appendChild(youSpan);
      valsDiv.appendChild(sep);
      valsDiv.appendChild(themSpan);
      pCard.appendChild(valsDiv);
      pillars.appendChild(pCard);
    }
    panel.appendChild(pillars);

    return panel;
  }

  /**
   * Copy text to clipboard using the modern API with a fallback
   * for browsers/contexts without it.
   */
  function copyToClipboard(text, callback) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(
        function() { callback(true); },
        function() { fallbackCopy(text, callback); }
      );
    } else {
      fallbackCopy(text, callback);
    }
  }

  function fallbackCopy(text, callback) {
    var ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    var ok = false;
    try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
    document.body.removeChild(ta);
    callback(ok);
  }

  /**
   * Use the Web Share API to challenge a friend, falling back to clipboard.
   * The shared payload is the test URL plus a teaser message including the code.
   */
  function shareWithFriend(result, code) {
    var pageUrl = window.location.origin + window.location.pathname;
    var tier = t('tier_' + result.tier);
    var body = t('share_friend_text', {
      score: result.total,
      tier: tier,
      code: code
    });
    var subject = t('share_friend_subject');
    var fullText = body + ' ' + pageUrl;

    if (navigator.share) {
      navigator.share({
        title: subject,
        text: fullText,
        url: pageUrl
      }).catch(function() {
        // User cancelled or error - fall back to clipboard
        copyToClipboard(fullText, function() {});
      });
    } else {
      copyToClipboard(fullText, function() {});
    }
  }

  /**
   * Generate a printable PDF of the user's Xtenia Score result.
   * Uses jsPDF (loaded on demand from CDN).
   *
   * @param {Object} result - Scoring engine output
   * @param {Object} answers - Original user answers (for name)
   * @param {string} code - The user's score code
   * @param {Function} onDone - Callback when PDF generation completes
   */
  function generateAndDownloadPDF(result, answers, code, onDone) {
    loadJsPDF(function(jsPDF) {
      if (!jsPDF) {
        if (typeof onDone === 'function') onDone();
        return;
      }

      try {
        var doc = new jsPDF({ unit: 'mm', format: 'a4' });
        var pageW = doc.internal.pageSize.getWidth();
        var name = (answers && answers.name) ? answers.name : '';
        var date = new Date();
        var dateStr = date.toLocaleDateString();

        // Brand color
        var brandRGB = [227, 61, 32];
        var darkRGB = [15, 18, 24];
        var textRGB = [40, 40, 40];

        // === Header bar ===
        doc.setFillColor(darkRGB[0], darkRGB[1], darkRGB[2]);
        doc.rect(0, 0, pageW, 30, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.text('XTENIA SCORE', 15, 18);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(180, 180, 180);
        doc.text('Advanced Calisthenics Assessment', 15, 24);

        // Date in top right
        doc.setFontSize(9);
        doc.text(dateStr, pageW - 15, 18, { align: 'right' });

        // === Greeting / Name ===
        var y = 45;
        doc.setTextColor(textRGB[0], textRGB[1], textRGB[2]);
        if (name) {
          doc.setFontSize(14);
          doc.setFont('helvetica', 'normal');
          doc.text('Result for ' + name, 15, y);
          y += 10;
        }

        // === Big Score ===
        doc.setFillColor(brandRGB[0], brandRGB[1], brandRGB[2]);
        doc.rect(15, y, pageW - 30, 35, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(48);
        doc.setFont('helvetica', 'bold');
        doc.text(String(result.total), pageW / 2, y + 22, { align: 'center' });
        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        doc.text('out of 1000', pageW / 2, y + 30, { align: 'center' });
        y += 45;

        // === Tier + Archetype ===
        doc.setTextColor(textRGB[0], textRGB[1], textRGB[2]);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('TIER', 15, y);
        doc.text('ARCHETYPE', pageW / 2, y);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(14);
        doc.text(t('tier_' + result.tier).toUpperCase(), 15, y + 7);
        doc.text(t('archetype_' + result.archetype), pageW / 2, y + 7);
        y += 18;

        // === Pillar Breakdown ===
        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.text('Pillar Breakdown', 15, y);
        y += 8;

        var pillars = ['strength', 'skill', 'mobility', 'endurance', 'movement'];
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        for (var i = 0; i < pillars.length; i++) {
          var p = pillars[i];
          var pVal = result.pillars[p] || 0;
          var pName = t('pillar_' + p);
          // Bar background
          doc.setFillColor(230, 230, 230);
          doc.rect(15, y - 3, pageW - 30, 5, 'F');
          // Bar fill
          doc.setFillColor(brandRGB[0], brandRGB[1], brandRGB[2]);
          var fillW = ((pageW - 30) * pVal) / 100;
          doc.rect(15, y - 3, fillW, 5, 'F');
          // Label
          doc.setTextColor(textRGB[0], textRGB[1], textRGB[2]);
          doc.text(pName + ': ' + pVal, 15, y + 7);
          y += 12;
        }
        y += 5;

        // === Fastest Unlock ===
        if (result.fastestUnlock) {
          doc.setFontSize(13);
          doc.setFont('helvetica', 'bold');
          doc.text('Fastest Unlock', 15, y);
          y += 7;
          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          var unlockName = t(result.fastestUnlock.skillKey);
          var days = result.fastestUnlock.days || 0;
          doc.text(unlockName + '   (~' + days + ' days)', 15, y);
          y += 12;
        }

        // === Ceiling ===
        if (result.ceiling && result.ceiling.nextTierKey) {
          doc.setFontSize(13);
          doc.setFont('helvetica', 'bold');
          doc.text('Your Ceiling', 15, y);
          y += 7;
          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          var nextTier = t('tier_' + result.ceiling.nextTierKey);
          var months = result.ceiling.months || 0;
          doc.text('At your current pace, you will reach ' + nextTier + ' in about ' + months + ' months.', 15, y);
          y += 12;
        }

        // === Score code (footer area) ===
        y = 270;
        doc.setDrawColor(200, 200, 200);
        doc.line(15, y, pageW - 15, y);
        y += 7;
        doc.setFontSize(9);
        doc.setTextColor(120, 120, 120);
        doc.text('Your code: ' + code, 15, y);
        doc.text('xtenia.com', pageW - 15, y, { align: 'right' });

        // Filename
        var safeName = (name || 'result').replace(/[^a-zA-Z0-9]/g, '_').substring(0, 20);
        var filename = 'xtenia-score-' + safeName + '-' + result.total + '.pdf';
        doc.save(filename);

        if (typeof onDone === 'function') onDone();
      } catch (err) {
        console.error('Xtenia Score PDF generation failed:', err);
        if (typeof onDone === 'function') onDone();
      }
    });
  }

  /**
   * Lazy-load jsPDF from CDN. Caches the constructor on window.
   */
  function loadJsPDF(callback) {
    if (window.jspdf && window.jspdf.jsPDF) {
      callback(window.jspdf.jsPDF);
      return;
    }
    var script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
    script.onload = function() {
      if (window.jspdf && window.jspdf.jsPDF) {
        callback(window.jspdf.jsPDF);
      } else {
        callback(null);
      }
    };
    script.onerror = function() {
      console.error('Xtenia Score: failed to load jsPDF');
      callback(null);
    };
    document.head.appendChild(script);
  }

})();